import { useEffect, useState, useRef } from 'react';
import { ChevronLeft, ChevronRight } from "lucide-react"
import styles from './Address.module.scss';

interface Doctor {
  id: string;
  name: string;
  office_id: string[] | number[];
}

interface ScheduleRow {
  Time: string;
  OfficeId: string | number;
  DoctorId: string | number;
  DoctorFullName?: string;
  FacilityId?: string | number;
  Price?: number;
  Services?: any;
  MinimumPrepaymentAmount: null;
}

interface AddressProps {
  productId?: number;
  officeIds?: string;
  doctorId?: string;
  addressOption: any[];
  schedule: any[];
  selectedDate?: Date;
  doctors?: Doctor[];
  value?: { time: string; address: string | null; id: string | null; desc: string | null; doctorId?: string | null; doctorName?: string | null };
  onChange: (val:  { time: string; address: string | null; id: string | null; idsSameName: any; officeSameNameDoctors: any; desc: string | null; doctorId: string | null; doctorName: string | null   }) => void;
}

const areaOffices = {
  "ul.Młynarska 42": " ( Wola )",
  "ul. Targowa 2": " ( Praga-Północ )",
  "ul.Józefa Dwernickiego 8/2": " ( Grzegórzki )",
  "ul.Władysława Wagnera 10": "",
  "Ul. Gdańska 1/4": " ( Śródmieście )",
  "ul.Przyjaźni 66": " ( Krzyki )",
  "ul.Starowiejska 50/132": " ( Letnica )",
  "ul. Naramowicka 31a": " ( Nowe Miasto )",
  "ul. Wolności 20": " ( Łabędy )",
  "ul.Warszawska 40": " ( od ul.Górniczej )"
};

type OfficeAddress = keyof typeof areaOffices;



const formatTime24 = (s: string): string => {
  try {
    const t = s.includes('T') ? s.split('T')[1] : s;
    return t.slice(0, 5);
  } catch {
    const d = new Date(s);
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(d.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  }
};

const Address = ({
  productId,
  officeIds,
  doctorId,
  addressOption,
  schedule,
  selectedDate,
  doctors = [],
  value,
  onChange
}: AddressProps) => {
  const [availableAddress, setAvailableAddress] = useState<any[]>([]);
  const [availableTimeForDoctor, setAvailableDatesTimeForDoctor] = useState<Record<string, ScheduleRow[]>>({});
  const [availableDoctors, setAvailableDoctors] = useState<Doctor[]>([]);
  const [timesSelectedDate, setTimesSelectedDate] = useState<ScheduleRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setForce] = useState(0);
  const forceUpdate = () => setForce(f => f + 1);

  const sliderRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [scrollThumb, setScrollThumb] = useState<Record<string, { left: number; width: number }>>({});

  const updateScrollThumb = (officeId: string) => {
    const el = sliderRefs.current[officeId];
    if (el) {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      if (scrollWidth <= clientWidth) {
        setScrollThumb((prev) => ({ ...prev, [officeId]: { left: 0, width: 100 } }));
        // Оновлюємо кастомний скролбар
        el.style.setProperty('--scroll-width', '0%');
      } else {
        const width = (clientWidth / scrollWidth) * 100;
        const left = (scrollLeft / scrollWidth) * 100;
        setScrollThumb((prev) => ({ ...prev, [officeId]: { left, width } }));
        // Оновлюємо кастомний скролбар
        el.style.setProperty('--scroll-width', `${width}%`);
        el.style.setProperty('--scroll-left', `${left}%`);
      }
    }
  };

  useEffect(() => {
    Object.keys(sliderRefs.current).forEach((officeId) => {
      const el = sliderRefs.current[officeId];
      if (el) {
        const handler = () => updateScrollThumb(officeId);
        el.addEventListener('scroll', handler);
        updateScrollThumb(officeId);
        return () => el.removeEventListener('scroll', handler);
      }
    });
  }, [officeIds, doctors, schedule, selectedDate]);

  // Force update при зміні дати для коректного оновлення canScrollLeft/canScrollRight
  useEffect(() => {
    setTimeout(() => {
      forceUpdate();
    }, 100);
  }, [selectedDate]);

  // Функції для скролу стрілками
  const handleScrollLeft = (officeId: string) => {
    const el = sliderRefs.current[officeId];
    if (el) {
      el.scrollBy({ left: -el.clientWidth, behavior: 'smooth' });
    }
  };
  const handleScrollRight = (officeId: string) => {
    const el = sliderRefs.current[officeId];
    if (el) {
      el.scrollBy({ left: el.clientWidth, behavior: 'smooth' });
    }
  };

  // Додаємо функції для визначення можливості скролу
  const canScrollLeft = (officeId: string) => {
    const el = sliderRefs.current[officeId];
    if (!el) return false;
    return el.scrollLeft > 0;
  };

  const canScrollRight = (officeId: string) => {
    const el = sliderRefs.current[officeId];
    if (!el) return false;
    return el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
  };

  // Фільтрація доступних адрес
  useEffect(() => {
    try {
      setIsLoading(true);
      setError(null);
      
      if (!officeIds) {
        setAvailableAddress([]);
        return;
      }
      let officeIdArr: string[] = [];
      if (typeof officeIds === 'string') {
        officeIdArr = officeIds
          .split(',')
          .map((id: string) => id.trim())
          .filter(Boolean);
      }
      
      const filteredAddresses = addressOption.filter((item: any) => officeIdArr.includes(String(item.id)));
     
      setAvailableAddress(filteredAddresses);
    } catch (err) {
      setError('Error filtering addresses');
      console.error('Error filtering addresses:', err);
    } finally {
      setIsLoading(false);
    }
  }, [addressOption, officeIds]);

  // Фільтрація доступних лікарів для вибраного міста
  useEffect(() => {
    try {
      if (!officeIds || doctors.length === 0) {
        setAvailableDoctors([]);
        return;
      }
      
      let officeIdArr: string[] = [];
      if (typeof officeIds === 'string') {
        officeIdArr = officeIds
          .split(',')
          .map((id: string) => id.trim())
          .filter(Boolean);
      }
      
      const filteredDoctors = doctors.filter((doctor: Doctor) =>
        doctor.office_id.some((office_id: any) =>
          officeIdArr.includes(String(office_id))
        )
      );
      
      setAvailableDoctors(filteredDoctors);
    } catch (err) {
      console.error('Error filtering doctors:', err);
    }
  }, [doctors, officeIds]);

  // Отримання часу для вибраної дати
  useEffect(() => {
    try {
      if (selectedDate && schedule.length > 0) {
        // Використовуємо локальну дату замість UTC
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;

        // Schedule вже відфільтрований в Form компоненті, тому просто шукаємо потрібну дату
        const found = schedule.find((item: any) => {
          let scheduleDate;
          if (item.Date.includes('T')) {
            scheduleDate = item.Date.split('T')[0];
          } else {
            scheduleDate = item.Date;
          }
          const isMatch = scheduleDate === dateStr;
          return isMatch;
        });
        setTimesSelectedDate(found ? found.Rows : []);
      } else {
        setTimesSelectedDate([]);
      }
    } catch (err) {
      console.error('Error getting time for selected date:', err);
      setTimesSelectedDate([]);
    }
  }, [schedule, selectedDate]);

  // Фільтрація доступного часу по лікарю та кабінету
  useEffect(() => {
    try {
      if (timesSelectedDate.length === 0) {
        setAvailableDatesTimeForDoctor({});
        return;
      }

      let filteredTimes = timesSelectedDate;

      // Якщо вибраний конкретний лікар (не "Dowolny lekarz"), фільтруємо тільки його час
      if (doctorId && doctorId !== '') {
       
        filteredTimes = filteredTimes.filter((row: ScheduleRow) => 
          String(row.DoctorId) === String(doctorId)
        );
      } else {
        // console.log('Показуємо всіх лікарів (Dowolny lekarz)');
      }

      // Групуємо час по кабінетам
      const groupedByOffice = filteredTimes.reduce((acc: Record<string, ScheduleRow[]>, row: ScheduleRow) => {
        const officeId = String(row.OfficeId);
        if (!acc[officeId]) {
          acc[officeId] = [];
        }
        acc[officeId].push(row);
        return acc;
      }, {});

      // Не об'єднуємо кабінети, а зберігаємо оригінальну структуру
      setAvailableDatesTimeForDoctor(groupedByOffice);
    } catch (err) {
      console.error('Error filtering available times:', err);
      setAvailableDatesTimeForDoctor({});
    }
  }, [timesSelectedDate, doctorId]);

  // Встановлення дефолтного значення: перший доступний слот у першому кабінеті
  useEffect(() => {
    try {
      // Якщо вже вибрано слот — нічого не робимо
      if (value && value.time) return;

      // Перший кабінет у списку (після фільтрації за містом)
      const unique = getUniqueOffices();
      if (!unique || unique.length === 0) return;

      const firstOffice = unique[0];
      if (!firstOffice) return;

      // Знаходимо всі кабінети з такою ж назвою
      const officesWithSameName = availableAddress.filter((addr) => addr.address === firstOffice.address);

      // Агрегуємо всі слоти часу для цих кабінетів
      let allOfficeTimes: ScheduleRow[] = [];
      officesWithSameName.forEach((office) => {
        const officeId = String(office.id);
        const officeTimes = availableTimeForDoctor[officeId] || [];
        allOfficeTimes = allOfficeTimes.concat(officeTimes);
      });

      // Прибираємо слоти, що вже минули відносно поточного часу
      const nowMs = Date.now();
      allOfficeTimes = allOfficeTimes.filter((row) => new Date(row.Time).getTime() >= nowMs);

      if (!allOfficeTimes || allOfficeTimes.length === 0) return;

      // Видаляємо дублі слотів з однаковим часом
      const uniqueTimes = new Map<string, ScheduleRow>();
      allOfficeTimes.forEach((row) => {
        const time = formatTime24(row.Time);
        if (!uniqueTimes.has(time)) {
          uniqueTimes.set(time, row);
        }
      });

      // Сортуємо за часом та беремо перший
      const sorted = Array.from(uniqueTimes.entries())
        .sort((a, b) => new Date(a[1].Time).getTime() - new Date(b[1].Time).getTime());

      if (sorted.length === 0) return;

      const [firstTimeStr, firstRow] = sorted[0];

      // Знаходимо дані кабінету для цього слоту
      const slotOfficeId = String(firstRow.OfficeId);
      const slotOffice = availableAddress.find((addr) => String(addr.id) === slotOfficeId);
      const officeName = slotOffice ? slotOffice.address : firstOffice.address;
      const officeDesc = slotOffice ? slotOffice.desk : firstOffice.desk;
      // Список ID кабінетів з однаковою назвою в межах вибраного міста (поточний ID перший)
      const sameNameOfficeIds = officesWithSameName.map(o => String(o.id));
      const orderedIdsForSameName = [slotOfficeId, ...sameNameOfficeIds.filter(id => id !== slotOfficeId)];
      const sameNameIdsAttr = orderedIdsForSameName.join(', ');
      // Список лікарів для кабінетів з однаковою назвою (поточний лікар перший)
      const uniqueDoctorsMap = new Map<string, { id: string; name: string }>();
      allOfficeTimes.forEach(r => {
        const dId = String(r.DoctorId);
        if (!uniqueDoctorsMap.has(dId)) {
          uniqueDoctorsMap.set(dId, { id: dId, name: r.DoctorFullName ?? '' });
        }
      });
      const doctorsInSameName = Array.from(uniqueDoctorsMap.values());
      const orderedDoctors = [
        { id: String(firstRow.DoctorId), name: firstRow.DoctorFullName ?? '' },
        ...doctorsInSameName.filter(d => String(d.id) !== String(firstRow.DoctorId))
      ];
      const doctorsSameNameAttr = JSON.stringify(orderedDoctors);

      onChange({
        time: String(firstTimeStr),
        id: slotOfficeId,
        idsSameName: sameNameIdsAttr,
        officeSameNameDoctors: doctorsSameNameAttr,
        address: officeName,
        desc: officeDesc,
        doctorId: String(firstRow.DoctorId ?? ''),
        doctorName: firstRow.DoctorFullName ?? ''
      });
    } catch (err) {
      console.error('Error setting default slot:', err);
    }
  }, [availableTimeForDoctor, availableAddress, doctorId, selectedDate]);


  // Функція для пошуку найближчого доступного терміну для кабінету
  const findNearestAvailableDate = (officeIds: string[]) => {
    try {
      if (!schedule || !Array.isArray(schedule) || officeIds.length === 0) {
        return null;
      }

      // Фільтруємо schedule для цих кабінетів
      const officeSchedule = schedule.filter(
        (item) =>
          item.Rows &&
          item.Rows.some(
            (row: any) =>
              officeIds.includes(String(row.OfficeId)) &&
              (!doctorId ||
                doctorId === '' ||
                String(row.DoctorId) === String(doctorId))
          )
      );

      if (officeSchedule.length === 0) return null;

      // Сортуємо за датою та знаходимо найближчу
      const sortedDates = officeSchedule
        .map((item) => new Date(item.Date))
        .sort((a, b) => a.getTime() - b.getTime());

      const nearestDate = sortedDates[0];
      if (!nearestDate) return null;

      // Форматуємо дату
      const year = nearestDate.getFullYear();
      const month = String(nearestDate.getMonth() + 1).padStart(2, '0');
      const day = String(nearestDate.getDate()).padStart(2, '0');
      const formattedDate = `${day}.${month}.${year}`;
      console.log('Nearest date found:', formattedDate);
      return formattedDate;
    } catch (err) {
      console.error('Error finding nearest date:', err);
      return null;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const selectedTime = e.target.value;
      const officeId = e.target.getAttribute('data-r-office-id-slot');
      const officesIdsSameName = e.target.getAttribute('data-r-offices-ids-same-name');
      const officeSameNameDoctors = e.target.getAttribute('data-r-office-doctors-same-name');
      const officeDesc = e.target.getAttribute('data-r-office-desc');
      const officeName = e.target.getAttribute('data-r-office-name');
      const doctorId = e.target.getAttribute('data-r-doctor-id-slot');
      const doctorName = e.target.getAttribute('data-r-doctor-name-slot');



      onChange({
        time: String(selectedTime),
        id: officeId,
        idsSameName: officesIdsSameName,
        officeSameNameDoctors: officeSameNameDoctors,
        address: officeName,
        desc: officeDesc,
        doctorId: doctorId,
        doctorName: doctorName
      });
    } catch (err) {
      console.error('Error handling time selection:', err);
    }
  };

  // Функція для фільтрації унікальних кабінетів (без дублів назв)
  const getUniqueOffices = () => {
    try {
      const uniqueOffices: any[] = [];
      const seenAddresses = new Set<string>();
      
      availableAddress.forEach(itemAddress => {
        if (!seenAddresses.has(itemAddress.address)) {
          seenAddresses.add(itemAddress.address);
          uniqueOffices.push(itemAddress);
        }
      });
      
     return uniqueOffices;
    } catch (err) {
      console.error('Error getting unique offices:', err);
      return [];
    }
  };

  const uniqueOffices = getUniqueOffices();


  // Показуємо завантаження
  if (isLoading) {
    return (
      <div className={styles['loading-container']}>
        <div className={styles['loading-spinner']}></div>
      </div>
    );
  }

  // Показуємо помилку
  if (error) {
    return (
      <div className={styles['error-container']}>
        <p className={styles['error-message']}>{error}</p>
        <button 
          type="button"
          className={styles['retry-button']}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.location.reload();
          }}
        >
          Spróbuj ponownie
        </button>
      </div>
    );
  }

  return (
    <div className={styles['address-container']}>
      {uniqueOffices.length === 0 ? (
        <div className={styles['no-offices']}>
          <p>Brak dostępnych biur, wybierz miasto.</p>
        </div>
      ) : (
        uniqueOffices.map((itemAddress: any, index: number) => {
          // Знаходимо всі кабінети з такою ж назвою
          const officesWithSameName = availableAddress.filter(addr => addr.address === itemAddress.address);
          
          // Збираємо всі терміни для кабінетів з такою ж назвою
          let allOfficeTimes: ScheduleRow[] = [];
          
          officesWithSameName.forEach(office => {
            const officeId = String(office.id);
            const officeTimes = availableTimeForDoctor[officeId] || [];
            allOfficeTimes = allOfficeTimes.concat(officeTimes);
          });

          // Прибираємо слоти, що вже минули відносно поточного часу
          const nowMs = Date.now();
          allOfficeTimes = allOfficeTimes.filter(row => new Date(row.Time).getTime() >= nowMs);

          // TEST not avalible Slot
          // console.log("allOfficeTimes", allOfficeTimes) 
          // const testSlot: ScheduleRow = {
          //     "Time": "2025-09-01T13:15:00",
          //     "DoctorId": 1,
          //     "DoctorFullName": "Vadym Moskalenko",
          //     "Price": 179,
          //     "OfficeId": 49,
          //     "Services": [
          //         {
          //             "IsOnline": false,
          //             "Id": 22,
          //             "Name": "Pierwsza Wizyta Stacionarna"
          //         }
          //     ],
          //     "MinimumPrepaymentAmount": null,
          //     "FacilityId": 1
          // };
          // // Додаємо тестовий слот перед унікалізацією
          // allOfficeTimes.unshift(testSlot);

          
          // Видаляємо дублі слотів з однаковим часом
          const uniqueTimes = new Map<string, ScheduleRow>();
          allOfficeTimes.forEach(row => {
            const time = formatTime24(row.Time);
            if (!uniqueTimes.has(time)) {
              uniqueTimes.set(time, row);
            }
          });
          
          // Конвертуємо назад в масив і сортуємо за часом
          allOfficeTimes = Array.from(uniqueTimes.values());
          allOfficeTimes.sort((a, b) => new Date(a.Time).getTime() - new Date(b.Time).getTime());
          
          console.log(`Office ${itemAddress.address} (${itemAddress.id}): ${allOfficeTimes.length} times`);
          console.log(itemAddress)

          const address: OfficeAddress = itemAddress.address as OfficeAddress;
          return (
            <div key={itemAddress.id} className={styles['office-container']}>
              <div className={styles['office-header']}>
                <h4>{itemAddress.address} <span>{areaOffices[address] || ''}</span></h4>
                
                
              </div>
              
              <div className={styles['schedule-container']}>
                <div className={styles['prev-slot']}>
                  <button
                    type="button"
                    className={
                      styles['slider-arrow']
                    }
                    onClick={() => handleScrollLeft(itemAddress.id)}
                    aria-label="Попередні слоти"
                    disabled={!canScrollLeft(itemAddress.id)}
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                </div>
                <div
                  className={styles['time-slots-container'] + ' ' + styles['always-scrollbar']}
                  ref={el => { sliderRefs.current[itemAddress.id] = el; }}
                >


                  {allOfficeTimes.length > 0 ? (
                    allOfficeTimes.map((row: ScheduleRow, timeIndex: number) => {
                      
                      const time = formatTime24(row.Time);
                      
                      // Знаходимо правильний кабінет для цього слоту
                      const slotOfficeId = String(row.OfficeId);
                      const slotOffice = availableAddress.find(addr => String(addr.id) === slotOfficeId);
                      const officeName = slotOffice ? slotOffice.address : itemAddress.address;
                      const officeDesc = slotOffice ? slotOffice.desk : itemAddress.desk;
                      // Список ID кабінетів з однаковою назвою в межах вибраного міста (поточний ID перший)
                      const sameNameOfficeIds = officesWithSameName.map(o => String(o.id));
                      const orderedIdsForSameName = [slotOfficeId, ...sameNameOfficeIds.filter(id => id !== slotOfficeId)];
                      const sameNameIdsAttr = orderedIdsForSameName.join(', ');
                      // Список лікарів для кабінетів з однаковою назвою (поточний лікар перший)
                      const rowsForSameDay = officesWithSameName
                        .flatMap(o => {
                          const officeIdSame = String(o.id);
                          return (availableTimeForDoctor[officeIdSame] || []);
                        });
                      const uniqueDoctorsMap = new Map<string, { id: string; name: string }>();
                      rowsForSameDay.forEach(r => {
                        const dId = String(r.DoctorId);
                        if (!uniqueDoctorsMap.has(dId)) {
                          uniqueDoctorsMap.set(dId, { id: dId, name: r.DoctorFullName ?? '' });
                        }
                      });
                      const doctorsInSameName = Array.from(uniqueDoctorsMap.values());
                      // const orderedDoctors = [
                      //   { id: String(row.DoctorId), name: row.DoctorFullName ?? '' },
                      //   ...doctorsInSameName.filter(d => String(d.id) !== String(row.DoctorId))
                      // ];
                      const doctorsSameNameAttr = JSON.stringify(doctorsInSameName);
                        
                      return (
                        <div
                          key={`${time}-${slotOfficeId}-${timeIndex}`}
                          className={styles['time-slot-wrapper']}
                        >
                          <input
                            type='radio'
                            id={`slot-r-${time}-${slotOfficeId}-${timeIndex}`}
                            name='address-slot'
                            value={time}
                            data-r-doctor-id-slot={row.DoctorId}
                            data-r-doctor-name-slot={row.DoctorFullName}
                            data-r-office-id-slot={slotOfficeId}
                            data-r-offices-ids-same-name={sameNameIdsAttr}
                            data-r-office-doctors-same-name={doctorsSameNameAttr}
                            data-r-office-name={officeName}
                            data-r-office-desc={officeDesc}
                            style={{display: 'none'}}
                            checked={
                              value?.time === time &&
                              String(value?.id) === String(slotOfficeId)
                            }
                            onChange={handleChange}
                            aria-label={`Вибір часу ${time} для кабінету ${officeName}`}
                          />
                          <label
                            htmlFor={`slot-r-${time}-${slotOfficeId}-${timeIndex}`}
                            className={styles['time-slot-r-item']}
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                const input = document.getElementById(
                                  `slot-r-${time}-${slotOfficeId}-${timeIndex}`
                                ) as HTMLInputElement;
                                if (input) {
                                  input.checked = true;
                                  input.dispatchEvent(
                                    new Event('change', {bubbles: true})
                                  );
                                }
                              }
                            }}
                          >
                            <span className={styles['time-text']}>{time}</span>
                          </label>
                        </div>
                      );
                    })
                  ) : (
                    <div className={styles['no-slots']}>
                      {(() => {
                        const sameNameOfficeIds = officesWithSameName.map((o) =>
                          String(o.id)
                        );
                        const nearestDate =
                          findNearestAvailableDate(sameNameOfficeIds);
                        if (nearestDate) {
                          return `Najbliższe terminy ${nearestDate}`;
                        }

                        if (doctorId && doctorId !== '' && doctorId !== '999') {
                          return 'Wybrany lekarz nie przyjmuje pod tym adresem';
                        }

                        return 'Brak dostępnych terminów';
                      })()}
                    </div>
                  )}
                  <div className={styles['time-slots-indicator']} />
                </div>
                {/* Кастомний скролбар під слотами */}
                <div className={styles['custom-scrollbar-container']}>
                  <div className={styles['custom-scrollbar-track']} />
                  {(!canScrollLeft(itemAddress.id) && !canScrollRight(itemAddress.id) ? '' : 
                  <div 
                    className={styles['custom-scrollbar-thumb']}
                    style={{
                      width: `${scrollThumb[itemAddress.id]?.width || 100}%`,
                      left: `${scrollThumb[itemAddress.id]?.left || 0}%`,
                    }}
                  />
                )}
                </div>
                <div className={styles['next-slot']}>
                  <button
                    type="button"
                    className={
                      styles['slider-arrow']
                    }
                    onClick={() => handleScrollRight(itemAddress.id)}
                    aria-label="Наступні слоти"
                    disabled={!canScrollRight(itemAddress.id)}
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                </div>

                {/* <div className={
                    styles['time-slots-indicator'] +
                    (!canScrollLeft(itemAddress.id) && !canScrollRight(itemAddress.id) ? ' ' + styles['time-slots-indicator-disabled'] : '')
                  } /> */}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default Address;
